import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Chip, Link as MuiLink, CircularProgress } from '@mui/material';
import StoreHeader from '../../components/layout/StoreHeader';
import CardCoverImage from '../../components/CardCoverImage';
import CardQuickViewModal from '../../components/CardQuickViewModal';
import HowItWorksModal from '../../components/HowItWorksModal';
import EmailRecoveryPrompt from '../../components/EmailRecoveryPrompt';
import { getCreations, resolvePendingCreation, removePendingCreation, updateCreationCover } from '../../lib/myCreations';

const TEAL = '#206884';
const CARD_NAV_STATE = { stockType: 'standard', price: 14.99 };
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Browse-away resolution (Phase B): a "creating…" placeholder polls the SAME async job endpoint the
// create page uses, then finalizes it into a real card so the finished design lands here on its own.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 180000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const getJSON = async (path) => (await fetch(`${API_BASE}${path}`, { method: 'GET' })).json();
const postJSON = async (path, body) =>
  (await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })).json();

// Friendly relative date for a saved creation (Today / Yesterday / Mon D).
const formatWhen = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(then)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// "Create another" tile — same dashed-teal affordance as the rail tile, opens the how-it-works modal.
const CreateAnotherTile = () => {
  const [howOpen, setHowOpen] = useState(false);
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setHowOpen(true)}
        aria-label="Create another card"
        data-testid="create-another-tile"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          aspectRatio: '5 / 7',
          p: 1.5,
          font: 'inherit',
          cursor: 'pointer',
          textAlign: 'center',
          border: '1.5px dashed #9fc0cb',
          borderRadius: '12px',
          backgroundColor: '#f3f7f8',
          '&:hover': { backgroundColor: 'rgba(32,104,132,0.10)' },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: TEAL,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: 25,
            lineHeight: 1,
          }}
        >
          +
        </Box>
        <Typography sx={{ fontWeight: 600, color: TEAL, fontSize: 14 }}>Create another</Typography>
      </Box>
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </>
  );
};

const CreationsPage = () => {
  const navigate = useNavigate();
  const [creations, setCreations] = useState([]);
  const [active, setActive] = useState(null); // creation currently in the quick-view modal

  const refresh = useCallback(() => setCreations(getCreations()), []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Resolve any in-progress ("creating…") placeholders: poll the background job, then finalize it into
  // a real card. Runs entirely client-side on the existing async transport — no new endpoints. Each
  // generationId is worked at most once (inFlight guard); mountedRef survives re-renders so a running
  // poll loop is only stopped on real unmount (not on every creations state change).
  const inFlight = useRef(new Set());
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    const finalize = async (pending) => {
      const genId = pending.generationId;
      const deadline = Date.now() + POLL_MAX_MS;
      while (mountedRef.current) {
        let done = null;
        try {
          const status = await getJSON(`/api/cards/generate/${genId}`);
          if (status && status.success && status.data) {
            if (status.data.status === 'done') done = status.data;
            else if (status.data.status === 'failed') throw new Error('failed');
          } else if (status && status.success === false) {
            throw new Error('failed');
          }
        } catch {
          removePendingCreation(genId);
          if (mountedRef.current) refresh();
          return;
        }

        if (done) {
          try {
            const option = (done.options && done.options[0]) || null;
            const result = await postJSON('/api/cards/from-generation', {
              selectedOptionId: option ? option.id : undefined,
              generationId: genId,
              provenance: done.provenance,
              coverText: pending.inputs?.coverText,
              styleId: pending.inputs?.styleId,
            });
            if (result && result.success && result.data?.cardId) {
              // Persist the STABLE served cover (coverImageId + non-expiring URL), not the option's
              // ephemeral presigned preview — otherwise the resolved tile expires to "preview unavailable".
              resolvePendingCreation(genId, {
                cardId: result.data.cardId,
                coverImageId: result.data.coverImageId || result.data.imageId || null,
                coverImageUrl: result.data.coverImageUrl || null,
              });
            } else {
              removePendingCreation(genId);
            }
          } catch {
            removePendingCreation(genId);
          }
          if (mountedRef.current) refresh();
          return;
        }

        if (Date.now() >= deadline) {
          removePendingCreation(genId);
          if (mountedRef.current) refresh();
          return;
        }
        await sleep(POLL_INTERVAL_MS);
      }
    };

    getCreations()
      .filter((c) => c.status === 'creating' && c.generationId && !inFlight.current.has(c.generationId))
      .forEach((pending) => {
        inFlight.current.add(pending.generationId);
        finalize(pending);
      });
  }, [creations, refresh]);

  // Self-heal old creations stuck on "preview unavailable": entries saved BEFORE the stable-hosting fix
  // persisted an expiring URL and NO coverImageId, so CardCoverImage has nothing to fall back to. On load
  // we look each such card's cover up by id and repair the stored entry to the stable reference, then
  // re-render. Lazy + non-blocking (render current state first), idempotent (only entries missing
  // coverImageId, each healed at most once), and never throws/spams — a truly cover-less card just keeps
  // the calm placeholder.
  const healing = useRef(new Set());
  useEffect(() => {
    const heal = async (cardId) => {
      try {
        const resp = await getJSON(`/api/cards/${cardId}/cover`);
        if (resp && resp.success && resp.data && (resp.data.coverImageId || resp.data.coverImageUrl)) {
          updateCreationCover(cardId, {
            coverImageId: resp.data.coverImageId,
            coverImageUrl: resp.data.coverImageUrl,
          });
          if (mountedRef.current) refresh();
        }
        // No cover (404/empty) → leave the entry as-is; it keeps the placeholder.
      } catch {
        // Network/parse failure → silent; try again on a future load. Never crash the page.
      }
    };

    getCreations()
      .filter((c) => c.cardId && !c.coverImageId && !healing.current.has(c.cardId))
      .forEach((c) => {
        healing.current.add(c.cardId);
        heal(c.cardId);
      });
  }, [creations, refresh]);

  const openPersonalize = (creation) => {
    if (!creation) return;
    setActive(null);
    navigate(`/personalize/${creation.cardId}`, {
      state: { ...CARD_NAV_STATE, coverImageId: creation.coverImageId, coverImageUrl: creation.coverImageUrl },
    });
  };

  return (
    <>
      <StoreHeader />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Typography component="h1" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 0.5 }}>
          My creations
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          The cards you’ve made. Tap one to keep working on it.
        </Typography>

        {/* Email recovery banner — until they give us their email. */}
        <Box sx={{ mb: 4 }}>
          <EmailRecoveryPrompt />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <CreateAnotherTile />

          {creations
            .filter((c) => c.status === 'creating')
            .map((c) => (
              <Box key={`pending-${c.generationId}`}>
                <Box
                  data-testid="creation-in-progress"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    width: '100%',
                    aspectRatio: '5 / 7',
                    p: 2,
                    textAlign: 'center',
                    borderRadius: '8px',
                    backgroundColor: '#f3f7f8',
                    border: '1.5px solid #e0e9ec',
                  }}
                >
                  <CircularProgress size={26} sx={{ color: TEAL }} />
                  <Typography sx={{ fontWeight: 600, color: TEAL, fontSize: 14 }}>Creating…</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    We’ll save it here when it’s ready.
                  </Typography>
                </Box>
              </Box>
            ))}

          {creations
            .filter((c) => c.cardId)
            .map((c) => (
            <Box key={c.cardId}>
              <Box
                component="button"
                type="button"
                onClick={() => setActive(c)}
                aria-label={`Open creation ${c.cardId}`}
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  p: 0,
                  border: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'grey.100',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
                }}
              >
                <CardCoverImage cardId={c.cardId} imageId={c.coverImageId} coverImageUrl={c.coverImageUrl} alt="Your card design" />
                <Chip
                  label="AI design"
                  size="small"
                  sx={{ position: 'absolute', top: 7, left: 7, backgroundColor: 'rgba(255,255,255,0.92)' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{formatWhen(c.createdAt)}</Typography>
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => openPersonalize(c)}
                  sx={{ fontSize: 12, color: TEAL, fontWeight: 500 }}
                >
                  Edit
                </MuiLink>
              </Box>
            </Box>
          ))}
        </Box>

        {creations.length === 0 && (
          <Typography color="text.secondary" sx={{ mt: 3, fontSize: '0.95rem' }}>
            You haven’t made any cards yet. Start one above — your designs will appear here.
          </Typography>
        )}
      </Container>

      <CardQuickViewModal
        open={!!active}
        onClose={() => setActive(null)}
        cardId={active ? active.cardId : null}
        imageId={active ? active.coverImageId : null}
        coverImageUrl={active ? active.coverImageUrl : null}
        alt="Your card design"
        ariaLabel="Creation preview"
        actionLabel="Personalize this card"
        onConfirm={() => openPersonalize(active)}
      />
    </>
  );
};

export default CreationsPage;
